import { dateFns as moment } from '@pcln/date-fns-utils'
import { logEvent } from './logGaEvent'
import { getEcommerceNode } from './logGaEvent-ecommerce'
import { toSnakeCase } from '../../../common/helpers/stringHelpers'
import { getDuplicatedFields } from './helpers/getDuplicatedFields'
import {
  getCabinClass,
  getAncillariesNode,
  replaceSpacesWithHyphens,
  replaceSpacesWithUnderscore,
} from './helpers/listingsHelpers'

/**
 * Builds item_list array for `ecommerce.items`
 * @param {Array} listings
 * @param {Object} searchLoadEventData - Concatenated Object of static and dynamic events attributes
 * @returns
 */
const getItemList = ({
  listings,
  searchLoadEventData,
  searchRequestParams,
  airportCodeParams,
  tripType,
  hasRecommendedListings,
}) => {
  const { ap, los, rebook_flag, rebook_type } = searchLoadEventData
  // 1-pass to filter out ads (sponsored, crossSell, etc.)
  return listings
    ?.filter((item) => item?.priceKey)
    ?.reduce((arr, itin) => {
      const { isExpressDeal, allFareBrandNames, listingPosition, fareBrands, voidWindowInfo } = itin
      let slices = []
      isExpressDeal ? (slices = itin?.slice) : (slices = itin?.slices)
      const { stops, timeFrame, connections, isCancellable, commonProps, upgrade_available, quantity } =
        getDuplicatedFields({
          slices,
          isExpressDeal,
          voidWindowInfo,
          allFareBrandNames,
          listingPosition,
          fareBrands,
          searchRequestParams,
          isViewItemList: true,
        })
      const allBadgesString = getBadgesString({
        slices,
        isExpressDeal,
        listingPosition,
        hasRecommendedListings,
      })

      if (isExpressDeal) {
        const maxStops = slices?.[0]?.maximumStops
        const price = itin?.price?.find((p) => p?.type === 'AVG_PRICE')?.amount
        const timeFrame = slices?.[0]?.partOfDay?.split(' ')?.[0]?.toLowerCase()
        arr.push({
          ...getEcommerceNode({
            ap,
            los,
            isExpressDeal: true,
            rebook_flag,
            rebook_type,
            airportCodeParams,
            tripType,
            stops: maxStops,
            timeFrame,
            allBadgesString,
          }),
          connections: maxStops,
          quantity,
          price,
          upgrade_available,
          ancillary: null,
          cancellation_flag: 'n',
          index: listingPosition,
          cabin_class: null,
          split_ticket_flag: itin?.isFused ? 'y' : 'n',
        })
      } else if (fareBrands?.length > 0) {
        const visibleFareBrands = fareBrands?.slice(0, 2)
        const cabinClass = getCabinClass(visibleFareBrands)
        const fb = visibleFareBrands?.[0]
        commonProps.price = fb?.price?.find((p) => p?.type === 'AVG_PRICE')?.amount
        commonProps.cabin_class = replaceSpacesWithHyphens(cabinClass)
        const ancillary = getAncillariesNode(fb?.ancillaries)
        const segments = slices?.[0]?.segments || []

        arr.push({
          ...getEcommerceNode({
            ap,
            los,
            segments,
            rebook_flag,
            rebook_type,
            airportCodeParams,
            isCheapest: slices?.[0]?.merchandisingBadges?.some(
              (badge) => badge?.title?.toLowerCase() === 'cheapest'
            ),
            tripType,
            stops,
            timeFrame,
            allBadgesString,
          }),
          quantity,
          connections,
          ancillary,
          upgrade_available,
          cancellation_flag: isCancellable ? 'y' : 'n',
          ...commonProps,
          split_ticket_flag: itin?.isFused ? 'y' : 'n',
        })
      }

      return arr
    }, [])
}

/**
 * Builds reusable static data for "view_item_list"
 * @param {props, state}
 * @returns {Object}
 */
const getStaticItemEventsData = ({
  search_name,
  tripType,
  cabinClass,
  departingFrom,
  returningFrom,
  airportsArray,
  searchRequestParams,
  ap,
  los,
  isReshopExperience,
  bundleType,
  inventoryAvailable,
  searchRequest,
}) => {
  const updatedLos = tripType === 'OW' ? 0 : los
  const departureState = replaceSpacesWithUnderscore(searchRequest?.airports?.[airportsArray[0]]?.stateName)
  const destinationState = replaceSpacesWithUnderscore(searchRequest?.airports?.[airportsArray[1]]?.stateName)
  const departureCountry = replaceSpacesWithUnderscore(
    searchRequest?.airports?.[airportsArray[0]]?.countryName
  )
  const destinationCountry = replaceSpacesWithUnderscore(
    searchRequest?.airports?.[airportsArray[1]]?.countryName
  )
  const isModifyNow = searchRequestParams?.userActionType === 'MODIFY'

  return {
    search_name,
    itinerary_type: toSnakeCase(tripType, { lowerCase: true }),
    inventory_available: inventoryAvailable,
    cabin_class: toSnakeCase(cabinClass, { lowerCase: true }),
    trip_start_airport: toSnakeCase(departingFrom?.airportCode, { lowerCase: true }),
    trip_start_city_id: String(departingFrom?.cityId), // city code (NYC)
    trip_start_city: toSnakeCase(departingFrom?.cityName, { lowerCase: true }),
    trip_start_state: departureState,
    trip_start_country: departureCountry,
    trip_end_airport: toSnakeCase(returningFrom?.airportCode, { lowerCase: true }),
    trip_end_city_id: String(returningFrom?.cityId),
    trip_end_city: toSnakeCase(returningFrom?.cityName, { lowerCase: true }),
    trip_end_state: destinationState,
    trip_end_country: destinationCountry,
    num_adults: Number(searchRequestParams['num-adults'] || 0),
    num_infants: Number(searchRequestParams['num-infants'] || 0),
    num_children: Number(searchRequestParams['num-children'] || 0),
    ap: ap,
    los: updatedLos,
    bundle_flag: bundleType ? 'y' : 'n',
    bundle_type: bundleType || null,
    page_name: 'listings',
    rebook_flag: isReshopExperience ? 'y' : 'n',
    rebook_type: isReshopExperience ? (isModifyNow ? 'exchange_now' : 'exchange_later') : null,
    product_name: 'air',
  }
}

const getLengthOfStay = ({ returnDate, travelDate }) => {
  const los = moment(returnDate).diff(travelDate, 'days')
  return isNaN(los) ? null : los
}

/**
 * Generates dynamic 'type' and 'filters_applied' tagging data on slice change for RT
 * @param {Number} activeTabId
 * @param {Array} filterHistory
 * @returns Object
 */
const getDynamicItemEventsData = ({ activeTabId, isSorted, isFiltersApplied }) => ({
  type: 'slice_' + activeTabId,
  filters_applied: isFiltersApplied ? 'y' : 'n' /*are any filters applied*/,
  sort_applied: isSorted != 'LOWEST_PRICE' ? 'y' : 'n',
})

/** Logs the "view_item_list" ga tagging event
 * @param {Array} listings
 * @param {Object} searchLoadEventData
 */
const logItemList = ({
  inventoryAvailable,
  activeTabId,
  isFiltersApplied,
  isSorted,
  searchRequestParams,
  cabinClass,
  isReshopExperience,
  hasExpressDeal,
  tripType,
  departingFrom,
  returningFrom,
  listings,
  bundleType,
  ap,
  airportsArray,
  travelDate,
  returnDate,
  searchRequest,
  hasRecommendedListings,
}) => {
  const los = getLengthOfStay({ travelDate, returnDate })
  let search_name = 'retail'
  if (hasExpressDeal) {
    search_name = listings[0].isFused ? 'split_express' : 'express'
  }
  const searchLoadEventData = getStaticItemEventsData({
    search_name,
    tripType,
    cabinClass,
    departingFrom,
    returningFrom,
    airportsArray,
    searchRequestParams,
    ap,
    los,
    isReshopExperience,
    bundleType,
    inventoryAvailable,
    searchRequest,
  })

  const dynamicEventData = getDynamicItemEventsData({
    activeTabId,
    isFiltersApplied,
    isSorted,
  })

  const itemList = getItemList({
    listings,
    searchLoadEventData,
    searchRequestParams,
    airportCodeParams: airportsArray,
    tripType,
    hasRecommendedListings,
  })

  logEvent('view_item_list', {
    ...searchLoadEventData,
    ...dynamicEventData,
    ecommerce: {
      items: itemList,
    },
  })
}

const getBadgesString = ({ slices = [], isExpressDeal, listingPosition, hasRecommendedListings }) => {
  if (isExpressDeal && listingPosition === 1 && hasRecommendedListings) {
    return 'recommended'
  }

  const uniqueBadges = [
    ...new Set(
      slices?.[0]?.merchandisingBadges?.map((badge) => badge.title.toLowerCase().replaceAll(' ', '-'))
    ),
  ] // removes unlikely but possible Recommended value duplicate

  return uniqueBadges.sort().join('_')
}

export { logItemList, getStaticItemEventsData, getDynamicItemEventsData, getBadgesString }
